import React, { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { Dropdown } from "../../design-system";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useDictation } from "../../common/useDictation";
import { useChatDemoFlag } from "../demo/demoFlags";
import {
    ArrowRightOutlined,
    AudioOutlined,
    CheckOutlined,
    DownOutlined,
    CopyOutlined,
    DislikeOutlined,
    LikeOutlined,
    LoadingOutlined,
    SendOutlined,
} from "@ant-design/icons";
import type { ChatIntro, ChatMessage } from "./types";
import classes from "./DrawerAssistant.module.less";

const BrandMark: React.FC = () => (
    <svg viewBox="0 0 110 61" fill="none" aria-label="Infinity Loop">
        <path
            d="M108.9083736,24.6111203L87.5611864,3.26393c-2.9444372-2.94444-7.7291564-2.94444-10.6735936,0l-36.6214753,36.4374449c-2.7604125,2.5763843-7.3611,5.1527686-11.0416518,5.1527686-4.0486047,0-8.0972093-1.840275-10.8576218-4.7847157-2.7604125-3.1284689-4.0486047-7.1770718-3.68055-11.4097047.7361111-6.8090172,6.2569343-12.145814,13.0659532-12.6978986h1.4722204c3.68055,0,7.1770753,1.2881922,9.7534596,3.8645782l6.4409625,6.4409625c.5520811.5520829,1.6562468.5520829,2.2083314,0l7.3611-7.3611c.5520811-.5520829.5520811-1.6562468,0-2.2083297l-4.4166593-4.4166602-2.0243067-2.0243006-.1840282-.1840273c-5.8888814-5.520825-13.8020625-8.2812375-21.7152471-7.5451273C13.3980991,3.8160124,2.908532,14.1215523,1.4363119,27.1875046c-.9201375,7.9131829,1.6562473,15.826364,6.993045,21.7152454,5.3367977,5.8888814,12.8819241,9.3854032,20.795107,9.3854032,10.121516,0,18.2187218-6.8090189,20.4270532-8.8333221l31.6527314-31.284675c.5520811-.5520829,1.6562503-.5520829,2.2083314,0l11.2256747,11.2256782c.5520811.5520829.5520811,1.6562468,0,2.2083314l-11.2256747,11.2256782c-.5520811.5520811-1.6562503.5520811-2.2083314,0l-8.4652622-8.4652657c-.5520811-.5520811-1.6562503-.5520811-2.2083314,0l-7.3611,7.3611c-.5520811.5520811-.5520811,1.6562468,0,2.2083314l13.6180378,13.6180343c1.4722186,1.6562468,3.4965183,2.3923561,5.3367933,2.3923561s3.8645747-.7361093,5.3368003-2.2083314l21.3471872-21.3471907c4.0486064-3.8645747,3.68055-8.0972076,0-11.7777576h0Z"
            fill="#24b2ee"
        />
    </svg>
);

const TypingDots: React.FC = () => (
    <div className={classes.typing}>
        <span />
        <span />
        <span />
    </div>
);

const MessageBubble: React.FC<{
    message: ChatMessage;
    onCopy?: () => void;
    copied?: boolean;
    onFeedback?: (sentiment: "positive" | "negative") => void;
}> = ({ message, onCopy, copied, onFeedback }) => {
    if (message.typing) {
        return (
            <div className={clsx(classes.message, classes.messageFromBot)}>
                <div className={clsx(classes.messageAvatar, classes.messageAvatarBrand)}>
                    <BrandMark />
                </div>
                <div className={classes.messageBody}>
                    <TypingDots />
                </div>
            </div>
        );
    }

    const isBot = message.role === "bot";
    // Only completed bot answers get copy/feedback actions.
    const showActions = isBot && message.kind === "answer" && !!message.text;

    return (
        <div
            className={clsx(
                classes.message,
                isBot ? classes.messageFromBot : classes.messageFromUser
            )}
        >
            <div className={clsx(classes.messageAvatar, isBot && classes.messageAvatarBrand)}>
                {isBot ? <BrandMark /> : "D"}
            </div>
            <div className={classes.messageBody}>
                {message.text &&
                    (isBot ? (
                        <div className={clsx(classes.bubbleContent, classes.markdownBody)}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ node, ...props }) => (
                                        <a {...props} target="_blank" rel="noreferrer" />
                                    ),
                                    // A per-vendor bid matrix is wider than the
                                    // drawer. Without a scroll boundary the table
                                    // sizes the bubble instead, which is what wraps
                                    // the header row into itself.
                                    table: ({ node, ...props }) => (
                                        <div className={classes.markdownTableWrap}>
                                            <table {...props} />
                                        </div>
                                    ),
                                }}
                            >
                                {message.text}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <div className={classes.bubbleContent}>{message.text}</div>
                    ))}
                {showActions && (
                    // The icons are decorative: the button label (or its
                    // aria-label) carries the meaning, and an exposed antd icon
                    // otherwise puts its own name ("copy", "like") into the
                    // button's accessible name. The row is also kept out of any
                    // hand-made selection: see .messageActions.
                    <div className={classes.messageActions}>
                        {onCopy && (
                            <button
                                type="button"
                                className={clsx(classes.actionButton, copied && classes.actionButtonDone)}
                                onClick={onCopy}
                            >
                                {copied ? (
                                    <>
                                        <CheckOutlined aria-hidden /> Copied
                                    </>
                                ) : (
                                    <>
                                        <CopyOutlined aria-hidden /> Copy
                                    </>
                                )}
                            </button>
                        )}
                        {onFeedback && (
                            <>
                                <button
                                    type="button"
                                    className={clsx(
                                        classes.actionButton,
                                        message.feedback === "positive" && classes.actionButtonDone
                                    )}
                                    onClick={() => onFeedback("positive")}
                                    disabled={!!message.feedback}
                                    title="Helpful"
                                    aria-label="Helpful"
                                >
                                    <LikeOutlined aria-hidden />
                                </button>
                                <button
                                    type="button"
                                    className={clsx(
                                        classes.actionButton,
                                        message.feedback === "negative" && classes.actionButtonDone
                                    )}
                                    onClick={() => onFeedback("negative")}
                                    disabled={!!message.feedback}
                                    title="Not helpful"
                                    aria-label="Not helpful"
                                >
                                    <DislikeOutlined aria-hidden />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const IntroPanel: React.FC<{
    intro: ChatIntro;
    disabled: boolean;
    onPick: (prompt: string) => void;
}> = ({ intro, disabled, onPick }) => (
    <div className={classes.intro}>
        <div className={classes.introHeader}>
            <div className={clsx(classes.messageAvatar, classes.messageAvatarBrand)}>
                <BrandMark />
            </div>
            <h2 className={classes.introTitle}>{intro.title}</h2>
        </div>
        <p className={classes.introDescription}>{intro.description}</p>
        <ul className={classes.introCapabilities}>
            {intro.capabilities.map((c) => (
                <li key={c}>{c}</li>
            ))}
        </ul>
        <div className={classes.introExamples}>
            <span className={classes.introExamplesLabel}>Try asking</span>
            {intro.examples.map((example) => (
                <button
                    key={example}
                    type="button"
                    className={classes.introExample}
                    onClick={() => onPick(example)}
                    disabled={disabled}
                >
                    <span>{example}</span>
                    <ArrowRightOutlined aria-hidden />
                </button>
            ))}
        </div>
    </div>
);

export interface ChatModeOption {
    value: string;
    label: string;
    /**
     * A few words on what the mode does, shown under the label in the menu.
     * Inline rather than behind a hover: the choice is being made right there,
     * and a tooltip is the wrong place to put the thing you need in order to
     * choose.
     */
    description?: string;
}

interface Props {
    messages: ChatMessage[];
    isStreaming: boolean;
    error: string | null;
    onSend: (text: string) => void;
    onStop: () => void;
    onFeedback: (messageId: string, sentiment: "positive" | "negative") => void;
    /** Mode picker in the composer. Omitted entirely when only one mode is available. */
    modes?: ChatModeOption[];
    mode?: string;
    onModeChange?: (mode: string) => void;
    /** Shown in place of the transcript while the conversation is empty. */
    intro?: ChatIntro;
}

export const AskChatPanel: React.FC<Props> = ({
    messages,
    isStreaming,
    error,
    onSend,
    onStop,
    onFeedback,
    modes,
    mode,
    onModeChange,
    intro,
}) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [inputText, setInputText] = useState("");
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const dictation = useDictation({ onTranscript: setInputText });
    const { listening } = dictation;
    // Chat demo: the send button works with an empty composer — each click
    // plays the next scripted exchange (see demoFlags).
    const [chatDemoEnabled] = useChatDemoFlag();

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const handleSend = () => {
        const text = inputText.trim();
        if ((!text && !chatDemoEnabled) || isStreaming) return;
        dictation.stop();
        onSend(text);
        setInputText("");
        dictation.resetTranscript();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleCopy = async (id: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // clipboard unavailable — swallow
        }
        setCopiedId(id);
        window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1800);
    };

    const placeholder = listening
        ? "Recording, speak naturally…"
        : chatDemoEnabled
        ? "Click send to walk through the report…"
        : mode === "coach"
        ? "Ask for counter arguments, or to practice the negotiation…"
        : "Ask me anything about this report…";

    return (
        <div className={classes.chatPanel}>
            <div className={classes.messages} ref={messagesContainerRef}>
                {messages.length === 0 && intro && (
                    <IntroPanel intro={intro} disabled={isStreaming} onPick={onSend} />
                )}
                {messages.map((m) => (
                    <MessageBubble
                        key={m.id}
                        message={m}
                        onCopy={m.text ? () => handleCopy(m.id, m.text!) : undefined}
                        copied={copiedId === m.id}
                        onFeedback={(sentiment) => onFeedback(m.id, sentiment)}
                    />
                ))}
                {error && (
                    <div className={classes.listeningHint} role="alert">
                        {error}
                    </div>
                )}
            </div>

            <div className={classes.composer}>
                <div className={clsx(classes.inputShell, listening && classes.inputShellRecording)}>
                    <textarea
                        className={classes.textArea}
                        placeholder={placeholder}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={3}
                    />
                    <div className={classes.inputControls}>
                        {/* `modes && modes.length > 1` IS the feature flag here.
                            This panel is deliberately flag-free: DrawerAssistant
                            owns the gate (the negotiation-coach flag, plus an
                            analysis to argue from) and
                            passes `modes` only when it opens, so an un-flagged
                            user gets no picker, no "New!" pointer and no label.
                            Keeping the check on the prop rather than reading the
                            flag again means the composer stays a presentational
                            component and one gate decides all three. */}
                        {/* TEMPORARY launch affordance. Static and always shown
                            by design; delete this block and .modeHint once the
                            coach has been socialized. */}
                        {modes && modes.length > 1 && (
                            <span className={classes.modeHint} aria-hidden="true">
                                New!
                                <ArrowRightOutlined />
                            </span>
                        )}
                        {modes && modes.length > 1 && (
                            <span className={classes.modePickerLabel}>Mode</span>
                        )}
                        {modes && modes.length > 1 && (
                            <span className={classes.modePickerWrap}>
                                <Dropdown
                                    trigger={["click"]}
                                    placement="topLeft"
                                    overlayClassName={classes.modeMenu}
                                    menu={{
                                        selectable: true,
                                        selectedKeys: mode ? [mode] : [],
                                        items: modes.map((m) => ({
                                            key: m.value,
                                            label: (
                                                <span className={classes.modeOption}>
                                                    <span className={classes.modeOptionName}>
                                                        {m.label}
                                                    </span>
                                                    {m.description && (
                                                        <span
                                                            className={
                                                                classes.modeOptionDescription
                                                            }
                                                        >
                                                            {m.description}
                                                        </span>
                                                    )}
                                                </span>
                                            ),
                                        })),
                                        onClick: ({ key }) => onModeChange?.(key),
                                    }}
                                >
                                    <button
                                        type="button"
                                        className={classes.modePicker}
                                        aria-label="Assistant mode"
                                    >
                                        <span>
                                            {modes.find((m) => m.value === mode)?.label ??
                                                modes[0].label}
                                        </span>
                                        <DownOutlined />
                                    </button>
                                </Dropdown>
                            </span>
                        )}
                        <button
                            type="button"
                            className={clsx(
                                classes.iconButton,
                                classes.micButton,
                                listening && classes.micButtonListening
                            )}
                            title={listening ? "Stop recording" : "Tap to record"}
                            onClick={dictation.toggle}
                        >
                            <AudioOutlined />
                        </button>
                        {listening && (
                            <div className={classes.listeningHint}>
                                <span className={classes.waveform}>
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                </span>
                                Recording…
                            </div>
                        )}
                        <div className={classes.spacer} />
                        {isStreaming ? (
                            <button
                                type="button"
                                className={clsx(classes.iconButton, classes.sendButton)}
                                title="Stop"
                                onClick={onStop}
                            >
                                <LoadingOutlined />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={clsx(classes.iconButton, classes.sendButton)}
                                title="Send"
                                onClick={handleSend}
                                disabled={!inputText.trim() && !chatDemoEnabled}
                            >
                                <SendOutlined />
                            </button>
                        )}
                    </div>
                </div>
                <p className={classes.disclaimer}>
                    Responses are AI-generated and may contain errors.
                </p>
            </div>
        </div>
    );
};
